import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Sparkles,
  Heart,
  Palette,
  Star,
  ExternalLink,
  Gift,
  CalendarDays,
  ShoppingBag,
  Wand2,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  MessageCircle,
} from 'lucide-react';

// Images are loaded directly from frontend/public
// Make sure these files exist in frontend/public:
// DavidAttenborough.png
// RowanAtkinson.png
// Adele.png
// DuaLipa.png

const paintings = [
  {
    name: 'David Attenborough',
    birthday: 'Saturday 8 May',
    wiki: 'https://en.wikipedia.org/wiki/David_Attenborough',
    image: '/DavidAttenborough.png',
    meaning:
      'A serene tribute to a Saturn-born soul, this birthday meaning painting carries the grounding strength of Saturday, the infinite rhythm of the number 8, and the gentle bloom of May. It becomes a visual story of calm resilience, patient wisdom, deep observation, and a life beautifully connected to nature.',
    energy: ['Wisdom', 'Nature', 'Balance'],
    symbols: ['Saturday energy', 'Number 8', 'May bloom'],
  },
  {
    name: 'Rowan Atkinson',
    birthday: 'Thursday 6 January',
    wiki: 'https://en.wikipedia.org/wiki/Rowan_Atkinson',
    image: '/RowanAtkinson.png',
    meaning:
      'Inspired by a Thursday birth under Jupiter, this painting reflects expansion, wit, intelligence, and generous creative energy. The number 6 brings harmony and character, while January adds clarity, stillness, and a quiet spark of winter magic.',
    energy: ['Joy', 'Harmony', 'Wit'],
    symbols: ['Thursday energy', 'Number 6', 'January clarity'],
  },
  {
    name: 'Adele',
    birthday: 'Thursday 5 May',
    wiki: 'https://en.wikipedia.org/wiki/Adele',
    image: '/Adele.png',
    meaning:
      'Born on a Thursday under Jupiter, this birthday painting reflects warmth, emotional abundance, creativity, and expressive beauty. The number 5 adds movement, freedom, and voice, while May gives the artwork a blooming, heartfelt atmosphere.',
    energy: ['Creativity', 'Voice', 'Freedom'],
    symbols: ['Thursday energy', 'Number 5', 'May blossom'],
  },
  {
    name: 'Dua Lipa',
    birthday: 'Tuesday 22 August',
    wiki: 'https://en.wikipedia.org/wiki/Dua_Lipa',
    image: '/DuaLipa.png',
    meaning:
      'Born on a Tuesday under Mars, this artwork carries confidence, rhythm, passion, and bold expression. The number 22 brings vision, structure, and creative power, while August adds golden warmth, motion, and magnetic possibility.',
    energy: ['Confidence', 'Passion', 'Vision'],
    symbols: ['Tuesday energy', 'Number 22', 'August fire'],
  },
];

const sizes = [
  {
    id: 'A4',
    label: 'A4 Birthday Meaning Painting',
    price: 10,
    description: 'A beautiful personal artwork, perfect for gifting, framing, or placing on a desk or wall.',
  },
  {
    id: 'A3',
    label: 'A3 Birthday Meaning Painting',
    price: 15,
    description: 'A larger and more immersive version, ideal as a statement birthday gift.',
  },
];

const defaultOrderForm = {
  customerName: '',
  customerEmail: '',
  birthDate: '',
  favouriteColours: '',
  personalSymbols: '',
  notes: '',
};

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const BirthdayMeaningPaintingsPage = () => {
  const [orderForm, setOrderForm] = useState(defaultOrderForm);
  const [selectedSize, setSelectedSize] = useState('A4');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    document.title =
      'Birthday Meaning Paintings | Custom Spiritual Art | Tantalizing.Art';

    const updateMetaTag = (name, content) => {
      let tag =
        document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);

      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(name.includes('og:') ? 'property' : 'name', name);
        document.head.appendChild(tag);
      }

      tag.setAttribute('content', content);
    };

    updateMetaTag(
      'description',
      'Order a custom birthday meaning painting inspired by your day of birth, numerology, planetary symbolism, month energy, and personal story.'
    );
    updateMetaTag(
      'keywords',
      'birthday meaning painting, custom birthday painting, personalised spiritual art, numerology art, astrology birthday gift, cosmic wall art, Tantalizing Art'
    );
    updateMetaTag(
      'og:title',
      'Birthday Meaning Paintings | Personalised Cosmic Art'
    );
    updateMetaTag(
      'og:description',
      'Turn your birthday into a custom artwork inspired by numerology, day energy, month symbolism, and your personal story.'
    );
    updateMetaTag('og:type', 'website');

    const existingLd = document.getElementById('birthday-meaning-jsonld');
    if (existingLd) existingLd.remove();

    const script = document.createElement('script');
    script.id = 'birthday-meaning-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Birthday Meaning Paintings',
      brand: {
        '@type': 'Brand',
        name: 'Tantalizing.Art',
      },
      description:
        'Personalised birthday meaning paintings inspired by numerology, day of birth, planetary energy, month symbolism, and personal custom requests.',
      image: [
        `${window.location.origin}/DavidAttenborough.png`,
        `${window.location.origin}/RowanAtkinson.png`,
        `${window.location.origin}/Adele.png`,
        `${window.location.origin}/DuaLipa.png`,
      ],
      offers: [
        {
          '@type': 'Offer',
          priceCurrency: 'GBP',
          price: '10',
          name: 'A4 Birthday Meaning Painting',
          availability: 'https://schema.org/InStock',
        },
        {
          '@type': 'Offer',
          priceCurrency: 'GBP',
          price: '15',
          name: 'A3 Birthday Meaning Painting',
          availability: 'https://schema.org/InStock',
        },
      ],
      url: window.location.href,
      seller: {
        '@type': 'Organization',
        name: 'Tantalizing.Art',
        email: 'tartalizing@gmail.com',
      },
    });

    document.head.appendChild(script);
  }, []);

  const selectedSizeInfo = sizes.find((item) => item.id === selectedSize) || sizes[0];

  const fadeInUp = {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.75, ease: 'easeOut' },
    },
  };

  const formatBirthdayLabel = () => {
    if (!orderForm.birthDate) return '';

    const date = new Date(`${orderForm.birthDate}T12:00:00`);

    if (Number.isNaN(date.getTime())) return orderForm.birthDate;

    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const day = date.toLocaleDateString('en-GB', { day: 'numeric' });
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.toLocaleDateString('en-GB', { year: 'numeric' });

    return `${weekday} ${day} ${month} ${year}`;
  };

  const buildMailtoHref = () => {
    const birthdayLabel = formatBirthdayLabel();

    const subject = encodeURIComponent('Birthday Meaning Painting Order');

    const body = encodeURIComponent(
      `Hello,

I would like to order a custom birthday meaning painting.

Name: ${orderForm.customerName || ''}
Email: ${orderForm.customerEmail || ''}
Birthday: ${birthdayLabel || orderForm.birthDate || ''}
Preferred size: ${selectedSizeInfo.id} (£${selectedSizeInfo.price})
Favourite colours: ${orderForm.favouriteColours || ''}
Personal symbols or details: ${orderForm.personalSymbols || ''}
Extra notes: ${orderForm.notes || ''}

Thank you.`
    );

    return `mailto:tartalizing@gmail.com?subject=${subject}&body=${body}`;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setOrderForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleStripeCheckout = async () => {
    setCheckoutError('');

    if (!orderForm.customerEmail || !orderForm.birthDate) {
      setCheckoutError(
        'Please add your email and full birthday before going to checkout.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = `${API_BASE_URL}/api/create-birthday-checkout-session/`;

      const payload = {
        product_type: 'birthday_meaning_painting',
        product_name: selectedSizeInfo.label,
        size: selectedSizeInfo.id,
        price_gbp: selectedSizeInfo.price,
        customer_name: orderForm.customerName,
        customer_email: orderForm.customerEmail,
        birth_date: orderForm.birthDate,
        birthday_display: formatBirthdayLabel(),
        favourite_colours: orderForm.favouriteColours,
        personal_symbols: orderForm.personalSymbols,
        notes: orderForm.notes,
        success_url: `${window.location.origin}/birthday-meaning-paintings?success=true`,
        cancel_url: `${window.location.origin}/birthday-meaning-paintings?cancelled=true`,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.detail ||
            'Stripe checkout could not be created. Please use the email order option.'
        );
      }

      const checkoutUrl = data.url || data.checkout_url || data.checkoutUrl;

      if (!checkoutUrl) {
        throw new Error(
          'Stripe checkout URL was not returned by the backend. Please check your Django endpoint.'
        );
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      setCheckoutError(
        error.message ||
          'Something went wrong with Stripe checkout. Please use the email order option.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const birthdayPreview = formatBirthdayLabel();

  return (
    <div className="min-h-screen bg-black text-white selection:bg-[#00B4A6] selection:text-white font-sans overflow-hidden">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-18%] left-[-12%] w-[55%] h-[55%] rounded-full bg-[#00B4A6]/12 blur-[130px]" />
        <div className="absolute bottom-[-20%] right-[-12%] w-[55%] h-[55%] rounded-full bg-[#C79A43]/12 blur-[130px]" />
        <div className="absolute top-[38%] left-[45%] w-[40%] h-[40%] rounded-full bg-purple-800/10 blur-[120px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_32%)]" />
      </div>

      <div className="fixed top-6 left-6 z-50">
        <Link
          to="/"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/55 border border-white/10 backdrop-blur-md text-zinc-300 hover:text-white hover:border-[#00B4A6]/50 transition-all duration-300 text-sm font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>

      <section className="relative z-10 pt-32 pb-20 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-20 items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00B4A6]/30 bg-[#00B4A6]/10 mb-8">
              <Sparkles className="w-4 h-4 text-[#00B4A6]" />
              <span className="text-xs font-bold tracking-widest uppercase text-[#DDFBF8]">
                Personalised Birthday Energy Art
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter mb-7 leading-[1.05]">
              Your birthday has a hidden image.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#C79A43] via-[#F4E7A1] to-[#00B4A6]">
                We turn it into a painting.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-zinc-400 mb-8 leading-relaxed max-w-2xl">
              A custom birthday meaning painting is created from the exact day
              you were born, your date number, your month, and the symbolic
              energy behind your birthday. It is not just decoration. It is a
              personal visual story, made for the person who receives it.
            </p>

            <div className="grid sm:grid-cols-3 gap-3 mb-8 max-w-2xl">
              {[
                'Day of birth',
                'Numerology',
                'Month symbolism',
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#00B4A6] shrink-0" />
                  <span className="text-sm text-zinc-300 font-semibold">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href="#order"
                className="flex items-center gap-2 px-8 py-4 bg-[#00B4A6] hover:bg-[#00796B] text-white rounded-full font-bold transition-all duration-300 shadow-[0_0_24px_rgba(0,180,166,0.25)] hover:shadow-[0_0_36px_rgba(0,180,166,0.45)] hover:-translate-y-1"
              >
                <ShoppingBag className="w-5 h-5" />
                Buy Your Birthday Painting
              </a>

              <a
                href="#examples"
                className="flex items-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-full font-bold transition-all duration-300 hover:-translate-y-1"
              >
                <Palette className="w-5 h-5" />
                See Examples
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-[#00B4A6]/20 to-[#C79A43]/20 blur-3xl -z-10 rounded-full" />

            <div className="bg-zinc-950/60 backdrop-blur-xl border border-white/10 rounded-[2rem] p-5 md:p-6 shadow-2xl">
              <div className="grid grid-cols-2 gap-4">
                {paintings.map((item) => (
                  <div
                    key={item.name}
                    className="rounded-3xl overflow-hidden border border-white/10 bg-black/70"
                  >
                    <div className="aspect-square p-2 bg-black">
                      <img
                        src={item.image}
                        alt={`${item.name} birthday meaning painting`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-[#C79A43] font-bold uppercase tracking-widest">
                        {item.birthday}
                      </p>
                      <p className="text-sm md:text-base font-black text-white mt-1">
                        {item.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-3xl bg-white/[0.04] border border-white/10 p-5">
                <div className="flex items-start gap-3">
                  <Gift className="w-6 h-6 text-[#C79A43] shrink-0 mt-1" />
                  <p className="text-zinc-300 leading-relaxed">
                    A birthday becomes more than a date. It becomes colour,
                    symbolism, mood, memory, and a gift that feels made for one
                    person only.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 py-16 px-6 border-y border-white/5 bg-white/[0.03] backdrop-blur-sm">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            {
              icon: CalendarDays,
              title: 'Your exact birthday',
              text: 'We use the day, date, month, and year to shape the meaning behind the artwork.',
            },
            {
              icon: Wand2,
              title: 'Your personal symbols',
              text: 'Add colours, moods, names, memories, animals, flowers, stars, or anything meaningful.',
            },
            {
              icon: Heart,
              title: 'A gift with a soul',
              text: 'Perfect for birthdays, partners, family, friends, or as a personal artwork for yourself.',
            },
          ].map((item) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="rounded-3xl border border-white/10 bg-black/35 p-7"
              >
                <Icon className="w-9 h-9 text-[#00B4A6] mb-5" />
                <h3 className="text-xl font-black text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">{item.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="relative z-10 py-20 px-6 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {sizes.map((size) => (
            <motion.div
              key={size.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className={`rounded-3xl p-8 text-center border transition-all duration-500 ${
                selectedSize === size.id
                  ? 'bg-[#00B4A6]/10 border-[#00B4A6]/60 shadow-[0_0_30px_rgba(0,180,166,0.15)]'
                  : 'bg-white/[0.04] border-white/10 hover:border-white/25'
              }`}
            >
              {size.id === 'A4' ? (
                <Palette className="w-10 h-10 text-[#00B4A6] mx-auto mb-4" />
              ) : (
                <Star className="w-10 h-10 text-[#C79A43] mx-auto mb-4" />
              )}

              <h3 className="text-2xl font-black mb-2">{size.id} Size</h3>
              <div
                className={`text-5xl font-black mb-4 ${
                  size.id === 'A4' ? 'text-[#00B4A6]' : 'text-[#C79A43]'
                }`}
              >
                £{size.price}
              </div>

              <p className="text-zinc-400 leading-relaxed mb-6">
                {size.description}
              </p>

              <button
                type="button"
                onClick={() => setSelectedSize(size.id)}
                className={`px-6 py-3 rounded-full font-bold transition-all duration-300 ${
                  selectedSize === size.id
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-white hover:bg-white/15'
                }`}
              >
                {selectedSize === size.id ? 'Selected' : `Choose ${size.id}`}
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="relative z-10 py-24 px-6 bg-white/[0.04] border-y border-white/5 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C79A43]/15 border border-[#C79A43]/30 mb-6">
            <Gift className="w-7 h-7 text-[#C79A43]" />
          </div>

          <h2 className="text-3xl md:text-5xl font-black mb-6 text-white">
            A birthday gift that does not feel random
          </h2>

          <p className="text-lg text-zinc-400 leading-relaxed">
            Many gifts are bought because they are available. This one is
            created because a specific person exists. Their birthday becomes
            the seed. Their colours, energy, memories, and symbols become the
            garden. The final painting becomes something they can keep, frame,
            and quietly recognise themselves inside.
          </p>
        </div>
      </section>

      <section
        id="examples"
        className="relative z-10 py-24 px-6 max-w-7xl mx-auto"
      >
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C79A43]/30 bg-[#C79A43]/10 mb-6">
            <Sparkles className="w-4 h-4 text-[#C79A43]" />
            <span className="text-xs font-bold tracking-widest uppercase text-[#F4E7A1]">
              Example Birthday Paintings
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl font-black mb-6">
            Famous birthdays, translated into visual meaning
          </h2>

          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            These examples show how a real birthday can become a symbolic
            artwork. Your own painting will be created around your birthday and
            the personal details you choose to add.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {paintings.map((item, index) => (
            <motion.article
              key={item.name}
              initial={{ opacity: 0, y: 34 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: index * 0.08 }}
              className="group bg-zinc-950/55 border border-white/10 rounded-[2rem] overflow-hidden hover:border-white/25 transition-all duration-500"
            >
              <div className="relative bg-black border-b border-white/10">
                <div className="aspect-[4/3] md:aspect-[16/11] p-4 md:p-6">
                  <img
                    src={item.image}
                    alt={`${item.name} custom birthday meaning painting for ${item.birthday}`}
                    className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                </div>

                <div className="absolute top-5 left-5 rounded-2xl bg-black/70 backdrop-blur-md border border-[#C79A43]/30 px-4 py-3">
                  <p className="text-[11px] uppercase tracking-widest text-[#C79A43] font-black">
                    Birthday
                  </p>
                  <p className="text-white font-black text-sm md:text-base">
                    {item.birthday}
                  </p>
                </div>
              </div>

              <div className="p-7 md:p-8">
                <a
                  href={item.wiki}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-2xl md:text-3xl font-black text-white hover:text-[#00B4A6] transition-colors mb-4"
                >
                  {item.name}
                  <ExternalLink className="w-5 h-5 opacity-50" />
                </a>

                <div className="flex flex-wrap gap-2 mb-5">
                  {item.energy.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#00B4A6]/10 text-[#00B4A6] rounded-full border border-[#00B4A6]/20"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid sm:grid-cols-3 gap-2 mb-6">
                  {item.symbols.map((symbol) => (
                    <div
                      key={symbol}
                      className="rounded-2xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm text-zinc-300"
                    >
                      {symbol}
                    </div>
                  ))}
                </div>

                <p className="text-zinc-400 leading-relaxed">
                  {item.meaning}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section
        id="order"
        className="relative z-10 py-24 px-6 max-w-6xl mx-auto border-t border-white/5"
      >
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-start">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00B4A6]/30 bg-[#00B4A6]/10 mb-6">
              <ShoppingBag className="w-4 h-4 text-[#00B4A6]" />
              <span className="text-xs font-bold tracking-widest uppercase text-[#DDFBF8]">
                Order Your Custom Painting
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-black mb-6">
              Add your birthday.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#C79A43] to-[#F4E7A1]">
                Let the artwork find its shape.
              </span>
            </h2>

            <p className="text-zinc-400 text-lg leading-relaxed mb-8">
              Add your full date of birth so the exact weekday can be included
              in the meaning. Then choose colours, symbols, or personal details
              you would love to see in the final artwork.
            </p>

            <div className="space-y-4">
              {[
                'Choose A4 for £10 or A3 for £15.',
                'Add your birthday and your preferred creative details.',
                'Pay securely through Stripe, if the checkout endpoint is enabled.',
                'Or email the same order details directly to tartalizing@gmail.com.',
              ].map((step, index) => (
                <div
                  key={step}
                  className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00B4A6] to-[#C79A43] flex items-center justify-center text-white font-black shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-zinc-300 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="rounded-[2rem] border border-white/10 bg-zinc-950/70 backdrop-blur-xl p-6 md:p-8 shadow-2xl"
          >
            <div className="flex items-center justify-between gap-4 mb-7">
              <div>
                <h3 className="text-2xl font-black text-white">
                  Birthday painting details
                </h3>
                <p className="text-zinc-500 mt-1">
                  The little cosmic paperwork before the magic begins.
                </p>
              </div>
              <ShieldCheck className="w-8 h-8 text-[#00B4A6] shrink-0" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              {sizes.map((size) => (
                <button
                  key={size.id}
                  type="button"
                  onClick={() => setSelectedSize(size.id)}
                  className={`text-left rounded-2xl border p-5 transition-all duration-300 ${
                    selectedSize === size.id
                      ? 'border-[#00B4A6] bg-[#00B4A6]/10'
                      : 'border-white/10 bg-white/[0.03] hover:border-white/25'
                  }`}
                >
                  <p className="text-white font-black">{size.id} Size</p>
                  <p className="text-3xl font-black text-[#C79A43] mt-1">
                    £{size.price}
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">
                    {selectedSize === size.id ? 'Selected' : 'Tap to select'}
                  </p>
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="customerName"
                  className="block text-sm font-bold text-zinc-300 mb-2"
                >
                  Your name
                </label>
                <input
                  id="customerName"
                  name="customerName"
                  type="text"
                  value={orderForm.customerName}
                  onChange={handleInputChange}
                  placeholder="Example: Sahana"
                  className="w-full rounded-2xl bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00B4A6]"
                />
              </div>

              <div>
                <label
                  htmlFor="customerEmail"
                  className="block text-sm font-bold text-zinc-300 mb-2"
                >
                  Your email
                </label>
                <input
                  id="customerEmail"
                  name="customerEmail"
                  type="email"
                  value={orderForm.customerEmail}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  className="w-full rounded-2xl bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00B4A6]"
                />
              </div>

              <div>
                <label
                  htmlFor="birthDate"
                  className="block text-sm font-bold text-zinc-300 mb-2"
                >
                  Full birthday
                </label>
                <input
                  id="birthDate"
                  name="birthDate"
                  type="date"
                  value={orderForm.birthDate}
                  onChange={handleInputChange}
                  className="w-full rounded-2xl bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00B4A6]"
                />
                <p className="text-xs text-zinc-500 mt-2">
                  The year helps calculate the exact weekday of birth.
                </p>

                {birthdayPreview && (
                  <div className="mt-3 rounded-2xl border border-[#C79A43]/25 bg-[#C79A43]/10 px-4 py-3">
                    <p className="text-xs uppercase tracking-widest text-[#C79A43] font-black">
                      Your birthday meaning will begin from
                    </p>
                    <p className="text-white font-black mt-1">
                      {birthdayPreview}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="favouriteColours"
                  className="block text-sm font-bold text-zinc-300 mb-2"
                >
                  Favourite colours
                </label>
                <input
                  id="favouriteColours"
                  name="favouriteColours"
                  type="text"
                  value={orderForm.favouriteColours}
                  onChange={handleInputChange}
                  placeholder="Example: turquoise, gold, deep blue"
                  className="w-full rounded-2xl bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00B4A6]"
                />
              </div>

              <div>
                <label
                  htmlFor="personalSymbols"
                  className="block text-sm font-bold text-zinc-300 mb-2"
                >
                  Personal symbols or details
                </label>
                <input
                  id="personalSymbols"
                  name="personalSymbols"
                  type="text"
                  value={orderForm.personalSymbols}
                  onChange={handleInputChange}
                  placeholder="Example: roses, moon, sea, mountains, piano"
                  className="w-full rounded-2xl bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00B4A6]"
                />
              </div>

              <div>
                <label
                  htmlFor="notes"
                  className="block text-sm font-bold text-zinc-300 mb-2"
                >
                  Anything else to add
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows="4"
                  value={orderForm.notes}
                  onChange={handleInputChange}
                  placeholder="Tell us if this is for a partner, friend, parent, child, or for yourself. Add any mood or story you want the painting to carry."
                  className="w-full rounded-2xl bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00B4A6] resize-none"
                />
              </div>
            </div>

            {checkoutError && (
              <div className="mt-5 rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-red-200 text-sm leading-relaxed">
                {checkoutError}
              </div>
            )}

            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={handleStripeCheckout}
                disabled={isSubmitting}
                className="flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#00B4A6] hover:bg-[#00796B] disabled:opacity-60 disabled:cursor-not-allowed text-white font-black transition-all duration-300 shadow-[0_0_24px_rgba(0,180,166,0.25)]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Opening Stripe
                  </>
                ) : (
                  <>
                    <ShoppingBag className="w-5 h-5" />
                    Pay £{selectedSizeInfo.price} with Stripe
                  </>
                )}
              </button>

              <a
                href={buildMailtoHref()}
                className="flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/8 hover:bg-white/12 border border-white/10 text-white font-black transition-all duration-300"
              >
                <Mail className="w-5 h-5" />
                Email Order
              </a>
            </div>

            <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
              If the Stripe endpoint is not connected yet, use the email order
              option. Your message will include the birthday, size, colours, and
              custom notes automatically.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 py-20 px-6 bg-white/[0.04] border-y border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <MessageCircle className="w-10 h-10 text-[#C79A43] mx-auto mb-5" />

          <h2 className="text-3xl md:text-4xl font-black mb-5">
            Want to ask before ordering?
          </h2>

          <p className="text-zinc-400 text-lg leading-relaxed mb-8">
            Send your birthday, preferred size, and any personal idea you have.
            We can shape the painting around a person, a mood, a memory, or a
            symbolic theme.
          </p>

          <a
            href={buildMailtoHref()}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#C79A43] hover:bg-[#a87f34] text-black rounded-full font-black transition-all duration-300 hover:-translate-y-1"
          >
            <Mail className="w-5 h-5" />
            Contact tartalizing@gmail.com
          </a>
        </div>
      </section>

      <footer className="relative z-10 py-12 px-6 border-t border-white/10 bg-black text-center">
        <h3 className="text-xl font-black text-white mb-2">Tantalizing.Art</h3>

        <p className="text-zinc-500 max-w-2xl mx-auto mb-8 leading-relaxed">
          Personalised birthday meaning paintings inspired by the poetry of
          your birth date, the symbolism of numbers, and the quiet theatre of
          the universe.
        </p>

        <p className="text-zinc-600 text-sm font-mono">
          © {new Date().getFullYear()} Tantalizing.Art • Birthday energy made visible
        </p>
      </footer>
    </div>
  );
};

export default BirthdayMeaningPaintingsPage;
